import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { handleCallback } from '../flows/AuthCallbackFlow';
import { useAuth } from '../hooks/useAuth';
import GradientBackground from '../components/GradientBackground';

export type AuthCallbackResult =
  | { status: 'success'; redirectTo?: string }
  | { status: 'not_authorized'; message?: string; redirectTo?: string }
  | { status: 'error'; message: string; redirectTo?: string };

export function parseOAuthCallbackParams(search: string): {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
} {
  const params = new URLSearchParams(search);
  return {
    code: params.get('code') ?? undefined,
    state: params.get('state') ?? undefined,
    error: params.get('error') ?? undefined,
    error_description: params.get('error_description') ?? undefined,
  };
}

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshFromStore } = useAuth();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const fullUrl = window.location.href;

    void (async () => {
      const result = await handleCallback(fullUrl);

      if (result.ok) {
        // Refresh auth state from the newly stored tokens
        await refreshFromStore();
        navigate(result.returnPath || '/app', { replace: true });
      } else if (result.kind === 'NOT_AUTHORIZED') {
        navigate('/?error=not_authorized', { replace: true });
      } else {
        navigate('/?error=callback_failed', { replace: true });
      }
    })();
  }, [navigate, location, refreshFromStore]);

  return (
    <GradientBackground>
      <div style={layoutStyle}>
        <div style={spinnerStyle} />
        <p style={textStyle}>Completing sign-in…</p>
      </div>
    </GradientBackground>
  );
}

/* ---- Styles ---- */

const layoutStyle: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '20px',
};

const spinnerStyle: CSSProperties = {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  border: '3px solid rgba(168,85,247,0.15)',
  borderTopColor: '#a855f7',
  animation: 'spin 0.9s linear infinite',
};

const textStyle: CSSProperties = {
  fontSize: '15px',
  color: 'rgba(255,255,255,0.55)',
  fontWeight: 500,
};
